import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { ClassWebsiteEditCourse } from "@shared/models/analytics/class-website"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "../../action-doc-builder"
import { assign, get, partial } from "lodash-es"
import type { Narrative } from "../create-narrative"
import defaultClassWebsiteActivity from "./default-class-website-activity"
import defaultClassWebsiteContext from "./default-class-website-context"
import defaultClassWebsiteModels from "./default-class-website-models"

interface Payload {
  planbookId: string
  websiteId: string
  courseId: string
}

export default function removeCourseFromClassWebsiteAction(payload: Payload): PreparedAction {
  return {
    name: "REMOVE_COURSE_FROM_CLASS_WEBSITE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload) {
  let patch = Patch.create("class-website", payload.websiteId).pull("relationships.courses.data", {
    id: payload.courseId,
    type: "course",
  }).patch

  return [patch]
}
function undoPatches(payload) {
  let patch = Patch.create("class-website", payload.websiteId).push("relationships.courses.data", {
    id: payload.courseId,
    type: "course",
  }).patch

  return [patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultClassWebsiteModels(payload, findFn, userId)
  let courseTitle = get(models, "course.attributes.title")
  let websiteTitle = get(models, "classWebsite.attributes.title")
  let websiteSlug = get(models, "classWebsite.attributes.slug")
  let context = assign({}, defaultClassWebsiteContext(payload, models), {
    websiteTitle,
    courseTitle,
    websiteSlug,
  })
  let newEvent: ClassWebsiteEditCourse = {
    ...createBlankEvent(),
    root_id: models.planbook.id,
    root_type: "planbook",
    date_plan_scheduled_for: null,

    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),

    primary_object_type: "class_website",
    primary_object_id: payload.websiteId,
    primary_object_specifier: null,
    primary_object_id_and_ancestor_ids: [payload.planbookId, payload.websiteId],

    adverbial_verb: "removing",

    adverbial_object_type: "course",
    adverbial_object_id: payload.courseId,
    adverbial_object_specifier: null,
    adverbial_object_id_and_ancestor_ids: [payload.planbookId, payload.courseId],
  }

  return {
    title: "Removed class from class website",
    titleForAnalytics: "Class Website - Remove Course",
    activity: defaultClassWebsiteActivity(payload, models, "removed"),
    description: `Removed ${courseTitle} from the "${websiteTitle}" website`,
    context,
    newAnalyticsEvents: [newEvent],
  }
}
